import api from 'api/config';

/**
 * Businesses Service for managing business profiles and related operations
 * Provides methods to interact with business-related API endpoints
 */
export const businessService = {
  /**
   * Get businesses with pagination
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.pageSize - Items per page (default: 20)
   * @returns {Promise} Response with businesses data
   */
  getBusinesses: async (params = {}) => {
    try {
      const queryParams = {
        page: params.page || 1,
        page_size: params.pageSize || 20,
      };

      const response = await api.get('/businesses', { params: queryParams });
      return {
        success: true,
        data: response.data.data,
        pagination: {
          total: response.data.data.total,
          page: response.data.data.page,
          pageSize: response.data.data.page_size,
          totalPages: response.data.data.total_pages,
          hasNext: response.data.data.has_next,
          hasPrev: response.data.data.has_prev,
        },
      };
    } catch (error) {
      console.error('Failed to get businesses:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to retrieve businesses',
      };
    }
  },

  /**
   * Get the current authenticated user's business
   * @returns {Promise} Response with user's business data or null if no business registered
   */
  getMyBusiness: async () => {
    try {
      const response = await api.get('/businesses/me');
      return {
        success: true,
        data: response.data.data,
      };
    } catch (error) {
      console.error('Failed to get user business:', error);
      return {
        success: false,
        error:
          error.response?.data?.error || 'Failed to retrieve user business',
      };
    }
  },

  /**
   * Get a single business by public ID
   * @param {string} publicId - Business public ID
   * @returns {Promise} Response with business details
   */
  getBusiness: async (publicId) => {
    try {
      if (!publicId) {
        throw new Error('Business public ID is required');
      }

      const response = await api.get(`/businesses/${publicId}`);
      return {
        success: true,
        data: response.data.data,
      };
    } catch (error) {
      console.error('Failed to get business:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to retrieve business',
      };
    }
  },

  /**
   * Create business profile
   * @param {Object} businessData - Business profile data
   * @param {string} businessData.name - Business name
   * @param {string} businessData.email - Business email
   * @param {string} businessData.businessType - Business type (required: sole_proprietorship, partnership, llc, corporation, s_corp, c_corp, nonprofit, other)
   * @param {string} businessData.phone - Phone number (optional)
   * @param {string} businessData.website - Website URL (optional)
   * @param {string} businessData.description - Business description (optional)
   * @param {string} businessData.industry - Industry (optional)
   * @param {string} businessData.companySize - Company size (optional)
   * @param {string} businessData.legalName - Legal business name (optional)
   * @param {string} businessData.taxId - Tax ID/EIN (optional)
   * @param {string} businessData.registrationNumber - Business registration number (optional)
   * @param {string} businessData.addressLine1 - Street address line 1 (optional)
   * @param {string} businessData.addressLine2 - Street address line 2 (optional)
   * @param {string} businessData.city - City (optional)
   * @param {string} businessData.state - State/Province (optional)
   * @param {string} businessData.postalCode - Postal/ZIP code (optional)
   * @param {string} businessData.country - Country (optional)
   * @returns {Promise} Response with created business
   */
  createBusiness: async (businessData) => {
    try {
      const payload = {
        // Required fields
        name: businessData.name,
        business_type: businessData.businessType,
        email: businessData.email,
        country: businessData.country,
        global_business_type_id: businessData.globalBusinessTypeId,
        // Optional fields
        ...(businessData.legalName && { legal_name: businessData.legalName }),
        ...(businessData.taxId && { tax_id: businessData.taxId }),
        ...(businessData.registrationNumber && {
          registration_number: businessData.registrationNumber,
        }),
        ...(businessData.phone && { phone: businessData.phone }),
        ...(businessData.website && { website: businessData.website }),
        ...(businessData.addressLine1 && {
          address_line1: businessData.addressLine1,
        }),
        ...(businessData.addressLine2 && {
          address_line2: businessData.addressLine2,
        }),
        ...(businessData.city && { city: businessData.city }),
        ...(businessData.state && { state: businessData.state }),
        ...(businessData.postalCode && {
          postal_code: businessData.postalCode,
        }),
        ...(businessData.description && {
          description: businessData.description,
        }),
        ...(businessData.industry && { industry: businessData.industry }),
        ...(businessData.companySize && {
          company_size: businessData.companySize,
        }),
      };

      const response = await api.post('/businesses', payload);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      console.error('Failed to create business:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to create business',
      };
    }
  },

  /**
   * Update business profile
   * @param {string} publicId - Business public ID
   * @param {Object} updates - Profile updates
   * @returns {Promise} Response with updated business
   */
  updateBusiness: async (publicId, updates) => {
    try {
      if (!publicId) {
        throw new Error('Business public ID is required');
      }

      const payload = {
        ...(updates.name && { name: updates.name }),
        ...(updates.phone && { phone: updates.phone }),
        ...(updates.location && { location: updates.location }),
        ...(updates.website && { website: updates.website }),
        ...(updates.description && { description: updates.description }),
        ...(updates.industry && { industry: updates.industry }),
        ...(updates.companySize && { company_size: updates.companySize }),
      };

      const response = await api.put(`/businesses/${publicId}`, payload);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      console.error('Failed to update business:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update business',
      };
    }
  },

  /**
   * Request business verification
   * @param {string} publicId - Business public ID
   * @param {Object} verificationData - Verification documents data
   * @param {string} verificationData.documentType - Type of document
   * @param {string} verificationData.documentUrl - Document URL
   * @param {string} verificationData.notes - Additional notes (optional)
   * @returns {Promise} Response with success message
   */
  requestVerification: async (publicId, verificationData) => {
    try {
      if (!publicId) {
        throw new Error('Business public ID is required');
      }

      const payload = {
        document_type: verificationData.documentType,
        document_url: verificationData.documentUrl,
        ...(verificationData.notes && { notes: verificationData.notes }),
      };

      const response = await api.post(
        `/businesses/${publicId}/verify`,
        payload
      );
      return {
        success: true,
        message: response.data.message,
      };
    } catch (error) {
      console.error('Failed to request verification:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to request verification',
      };
    }
  },

  /**
   * Get contracts for a business
   * @param {string} publicId - Business public ID
   * @returns {Promise} Response with business contracts
   */
  getBusinessContracts: async (publicId) => {
    try {
      if (!publicId) {
        throw new Error('Business public ID is required');
      }

      const response = await api.get(`/businesses/${publicId}/contracts`);
      return {
        success: true,
        data: response.data.data,
      };
    } catch (error) {
      console.error('Failed to get business contracts:', error);
      return {
        success: false,
        error:
          error.response?.data?.error ||
          'Failed to retrieve business contracts',
      };
    }
  },
};

/**
 * Utility functions for businesses
 */
export const businessesUtils = {
  /**
   * Format business data for display
   * @param {Object} business - Raw business data from API
   * @returns {Object} Formatted business data
   */
  formatBusiness: (business) => {
    if (!business) return null;

    return {
      ...business,
      createdAt: new Date(business.created_at),
      updatedAt: new Date(business.updated_at),
      verifiedAt: business.verified_at ? new Date(business.verified_at) : null,
    };
  },

  /**
   * Validate business data
   * @param {Object} businessData - Business data to validate
   * @returns {Object} Validation result with isValid and errors
   */
  validateBusinessData: (businessData) => {
    const errors = [];

    if (!businessData.name || businessData.name.trim().length === 0) {
      errors.push('Business name is required');
    }

    if (
      !businessData.email ||
      !businessData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)
    ) {
      errors.push('Valid email is required');
    }

    if (businessData.website && !businessData.website.match(/^https?:\/\/.+/)) {
      errors.push('Website must be a valid URL');
    }

    if (businessData.phone && !businessData.phone.match(/^[\d\s\-\+\(\)]+$/)) {
      errors.push('Phone number contains invalid characters');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  },

  /**
   * Validate verification data
   * @param {Object} verificationData - Verification data to validate
   * @returns {Object} Validation result
   */
  validateVerificationData: (verificationData) => {
    const errors = [];

    if (
      !verificationData.documentType ||
      verificationData.documentType.trim().length === 0
    ) {
      errors.push('Document type is required');
    }

    if (
      !verificationData.documentUrl ||
      !verificationData.documentUrl.match(/^https?:\/\/.+/)
    ) {
      errors.push('Valid document URL is required');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  },

  /**
   * Get company size display name
   * @param {string} size - Company size code
   * @returns {string} Display name
   */
  getCompanySizeName: (size) => {
    const sizes = {
      '1-10': '1-10 employees',
      '11-50': '11-50 employees',
      '51-200': '51-200 employees',
      '201-500': '201-500 employees',
      '501-1000': '501-1000 employees',
      '1000+': '1000+ employees',
      startup: 'Startup',
      small: 'Small Business',
      medium: 'Medium Business',
      large: 'Large Enterprise',
    };

    return sizes[size] || size;
  },

  /**
   * Get verification status badge
   * @param {boolean} isVerified - Verification status
   * @returns {Object} Badge configuration
   */
  getVerificationBadge: (isVerified) => {
    if (isVerified) {
      return {
        text: 'Verified',
        color: '#10b981', // green
        icon: 'check-circle',
      };
    }

    return {
      text: 'Unverified',
      color: '#6b7280', // gray
      icon: 'alert-circle',
    };
  },

  /**
   * Calculate business statistics
   * @param {Object} business - Business object with contracts
   * @returns {Object} Business statistics
   */
  calculateStatistics: (business) => {
    if (!business || !business.contracts) {
      return {
        totalContracts: 0,
        activeContracts: 0,
        completedContracts: 0,
        totalSpent: 0,
        averageContractValue: 0,
      };
    }

    const stats = {
      totalContracts: business.contracts.length,
      activeContracts: 0,
      completedContracts: 0,
      totalSpent: 0,
    };

    business.contracts.forEach((contract) => {
      if (contract.status === 'in_progress') {
        stats.activeContracts++;
      } else if (contract.status === 'completed') {
        stats.completedContracts++;
      }

      if (contract.amount_usd) {
        stats.totalSpent += contract.amount_usd;
      }
    });

    stats.averageContractValue =
      stats.totalContracts > 0 ? stats.totalSpent / stats.totalContracts : 0;

    return stats;
  },

  /**
   * Generate business summary
   * @param {Object} business - Business object
   * @returns {string} Business summary text
   */
  generateSummary: (business) => {
    if (!business) return '';

    const parts = [];

    if (business.industry) {
      parts.push(business.industry);
    }

    if (business.company_size) {
      parts.push(businessesUtils.getCompanySizeName(business.company_size));
    }

    if (business.location) {
      parts.push(business.location);
    }

    if (business.is_verified) {
      parts.push('Verified');
    }

    return parts.join(' • ');
  },

  /**
   * Check if business profile is complete
   * @param {Object} business - Business object
   * @returns {Object} Completion status with missing fields
   */
  checkProfileCompleteness: (business) => {
    const requiredFields = [
      'name',
      'email',
      'description',
      'website',
      'location',
      'industry',
      'company_size',
    ];
    const missingFields = [];

    requiredFields.forEach((field) => {
      if (!business[field]) {
        missingFields.push(field.replace('_', ' '));
      }
    });

    const completionPercentage = Math.round(
      ((requiredFields.length - missingFields.length) / requiredFields.length) *
        100
    );

    return {
      isComplete: missingFields.length === 0,
      completionPercentage,
      missingFields,
    };
  },

  /**
   * Format business type
   * @param {Object} business - Business object
   * @returns {string} Business type description
   */
  getBusinessType: (business) => {
    if (!business) return 'Unknown';

    if (business.is_verified) {
      return 'Verified Business';
    }

    if (
      business.company_size === 'startup' ||
      business.company_size === '1-10'
    ) {
      return 'Startup';
    }

    if (
      business.company_size === 'large' ||
      business.company_size === '1000+'
    ) {
      return 'Enterprise';
    }

    return 'Business';
  },
};

export default businessService;
